import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useModalQueue } from './ModalQueueContext';
import { useWelcomeOffer } from './WelcomeOfferContext';
import { apiClient } from '@/lib/api-client';
import { paymentStore } from '@/stores/paymentStore';

const shouldSkipGeoComply = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

interface ScheduledOffer {
  id: number;
  title: string;
  gc_amount: number;
  sc_amount: number;
  gc_bonus_amount: number;
  sc_bonus_amount: number;
  price: number;
  original_price: number | null;
  cadence_type: string;
  resets_at: string | null;
  can_purchase: boolean;
  purchases_remaining: number;
  group_id: number | null;
  is_welcome_offer: boolean;
}

interface ScheduledOfferGroup {
  id: number;
  title: string;
  offers: ScheduledOffer[];
  is_welcome_group: boolean;
}

interface DailyOfferContextType {
  showDailyOfferModal: boolean;
  setShowDailyOfferModal: (show: boolean) => void;
  currentDailyOffer: ScheduledOffer | null;
  currentDailyGroup: ScheduledOfferGroup | null;
  hasMoreDailyOffers: boolean;
  dismissCurrentOffer: () => void;
  checkAndShowDailyOffer: () => Promise<void>;
}

const DailyOfferContext = createContext<DailyOfferContextType | undefined>(undefined);

export const useDailyOffer = () => {
  const context = useContext(DailyOfferContext);
  if (context === undefined) {
    throw new Error('useDailyOffer must be used within a DailyOfferProvider');
  }
  return context;
};

const DAILY_OFFER_SHOWN_KEY = 'daily_offer_shown_session';

export const DailyOfferProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, session, isGeoComplyCompliant } = useAuth();
  const { requestShow, notifyDismissed, shouldShow, hasHigherPriorityModal } = useModalQueue();
  const { showWelcomeOfferModal } = useWelcomeOffer();
  
  const [dailyGroupsQueue, setDailyGroupsQueue] = useState<ScheduledOfferGroup[]>([]);
  const [dailyOffersQueue, setDailyOffersQueue] = useState<ScheduledOffer[]>([]);
  
  const [currentDailyGroup, setCurrentDailyGroup] = useState<ScheduledOfferGroup | null>(null);
  const [currentDailyOffer, setCurrentDailyOffer] = useState<ScheduledOffer | null>(null);
  
  const [hasChecked, setHasChecked] = useState(false);
  const [isEligible, setIsEligible] = useState(false);
  const [isTemporarilyHidden, setIsTemporarilyHidden] = useState(false);
  
  const hasMoreDailyOffers = dailyGroupsQueue.length > 0 || dailyOffersQueue.length > 0;

  const loadNextOffer = useCallback(() => {
    if (dailyGroupsQueue.length > 0) {
      const [nextGroup, ...remainingGroups] = dailyGroupsQueue;
      setCurrentDailyGroup(nextGroup);
      setCurrentDailyOffer(null);
      setDailyGroupsQueue(remainingGroups);
      return true;
    }
    
    if (dailyOffersQueue.length > 0) {
      const [nextOffer, ...remainingOffers] = dailyOffersQueue;
      setCurrentDailyOffer(nextOffer);
      setCurrentDailyGroup(null);
      setDailyOffersQueue(remainingOffers);
      return true;
    }
    
    sessionStorage.setItem(DAILY_OFFER_SHOWN_KEY, 'true');
    setIsEligible(false);
    return false;
  }, [dailyGroupsQueue, dailyOffersQueue]);
  
  const dismissCurrentOffer = useCallback(() => {
    setIsTemporarilyHidden(false);
    setCurrentDailyGroup(null);
    setCurrentDailyOffer(null);
    notifyDismissed('DAILY_OFFER');
    
    setTimeout(() => {
      if (loadNextOffer()) {
        if (!paymentStore.getState().isOpen) {
          requestShow('DAILY_OFFER');
        }
      }
    }, 100);
  }, [notifyDismissed, loadNextOffer, requestShow]);
  
  useEffect(() => {
    let wasOpen = paymentStore.getState().isOpen;
    
    const unsubscribe = paymentStore.subscribe(() => {
      const isOpen = paymentStore.getState().isOpen;
      
      if (wasOpen && !isOpen && isEligible && (currentDailyOffer || currentDailyGroup)) {
        requestShow('DAILY_OFFER');
      }
      
      wasOpen = isOpen;
    });
    
    return unsubscribe;
  }, [isEligible, currentDailyOffer, currentDailyGroup, requestShow]);

  const checkAndShowDailyOffer = useCallback(async () => {
    if (!user || !session) return;
    if (!user.email_verified) return;

    const hasShownThisSession = sessionStorage.getItem(DAILY_OFFER_SHOWN_KEY);
    if (hasShownThisSession) return;

    try {
      const data = await apiClient.get('/coins/packages');
      
      if (!data || (!data.scheduledOffers && !data.success)) return;

      const scheduledOffers = data.scheduledOffers || { groups: [], ungrouped: [] };
      
      const dailyUngrouped = (scheduledOffers.ungrouped || []).filter(
        (offer: ScheduledOffer) => 
          offer.cadence_type === 'daily' && 
          !offer.is_welcome_offer && 
          offer.can_purchase !== false && 
          (offer.purchases_remaining ?? 1) > 0
      );
      
      const dailyGrouped = (scheduledOffers.groups || []).filter(
        (group: ScheduledOfferGroup) => {
          const hasDailyOffer = group.offers.some(
            (o: ScheduledOffer) => o.cadence_type === 'daily' && !o.is_welcome_offer
          );
          const hasPurchasableOffer = group.offers.some(
            (o: ScheduledOffer) => o.can_purchase !== false && (o.purchases_remaining ?? 1) > 0
          );
          return hasDailyOffer && hasPurchasableOffer && !group.is_welcome_group;
        }
      );

      if (dailyUngrouped.length > 0 || dailyGrouped.length > 0) {
        setIsEligible(true);
        
        if (dailyGrouped.length > 0) {
          const [firstGroup, ...remainingGroups] = dailyGrouped;
          setCurrentDailyGroup(firstGroup);
          setCurrentDailyOffer(null);
          setDailyGroupsQueue(remainingGroups);
          setDailyOffersQueue(dailyUngrouped);
        } else if (dailyUngrouped.length > 0) {
          const [firstOffer, ...remainingOffers] = dailyUngrouped;
          setCurrentDailyOffer(firstOffer);
          setCurrentDailyGroup(null);
          setDailyOffersQueue(remainingOffers);
        }
        
        if (!paymentStore.getState().isOpen && !hasHigherPriorityModal('DAILY_OFFER')) {
          requestShow('DAILY_OFFER');
        }
      }
    } catch {
      // Silently fail
    }
  }, [user, session, requestShow, hasHigherPriorityModal]);

  useEffect(() => {
    const geoComplyPassed = shouldSkipGeoComply() || isGeoComplyCompliant === true;
    
    if (user && session && user.email_verified && geoComplyPassed && !hasChecked && !showWelcomeOfferModal) {
      checkAndShowDailyOffer();
      setHasChecked(true);
    } else if (!user) {
      setHasChecked(false);
      setIsEligible(false);
      setDailyGroupsQueue([]);
      setDailyOffersQueue([]);
      setCurrentDailyGroup(null);
      setCurrentDailyOffer(null);
      if (sessionStorage.getItem(DAILY_OFFER_SHOWN_KEY)) {
        sessionStorage.removeItem(DAILY_OFFER_SHOWN_KEY);
      }
    }
  }, [user, session, isGeoComplyCompliant, hasChecked, checkAndShowDailyOffer, showWelcomeOfferModal]);

  const queueShowsModal = shouldShow('DAILY_OFFER');
  const showDailyOfferModal = queueShowsModal && !isTemporarilyHidden;

  const setShowDailyOfferModal = useCallback((show: boolean) => {
    if (show) {
      setIsTemporarilyHidden(false);
    } else {
      setIsTemporarilyHidden(true);
    }
  }, []);

  const value = {
    showDailyOfferModal,
    setShowDailyOfferModal,
    currentDailyOffer,
    currentDailyGroup,
    hasMoreDailyOffers,
    dismissCurrentOffer,
    checkAndShowDailyOffer,
  };

  return (
    <DailyOfferContext.Provider value={value}>
      {children}
    </DailyOfferContext.Provider>
  );
};
